import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  TextField,
  CircularProgress,
  Typography,
  IconButton,
  ImageList,
  ImageListItem,
  Popper,
  Paper,
  ClickAwayListener,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { GiphyFetch } from '@giphy/js-fetch-api';

// Initialize Giphy client with API key
const gf = new GiphyFetch('XqTzmVqlBfVaXH0wsWxVrLf7Pk8hkfBS');

const GIFS_PER_PAGE = 20;

const GifPicker = ({ open, onClose, onGifSelect, anchorEl, position = 'bottom-start' }) => {
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Function to fetch trending GIFs
  const fetchTrendingGifs = useCallback(async (currentOffset = 0) => {
    try {
      setLoading(true);
      const { data, pagination } = await gf.trending({
        limit: GIFS_PER_PAGE,
        offset: currentOffset,
        rating: 'g',
      });

      setGifs(prevGifs => currentOffset === 0 ? data : [...prevGifs, ...data]);
      setOffset(currentOffset + data.length);
      setHasMore(!!pagination && pagination.total_count > currentOffset + data.length);
      setError(null);
    } catch (err) {
      console.error('Error fetching trending gifs:', err);
      setError('Failed to load GIFs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Function to search for GIFs
  const searchGifs = useCallback(async (query, currentOffset = 0) => {
    if (!query) {
      await fetchTrendingGifs();
      return;
    }

    try {
      setLoading(true);
      const { data, pagination } = await gf.search(query, {
        limit: GIFS_PER_PAGE,
        offset: currentOffset,
        rating: 'g',
        lang: 'en',
      });

      setGifs(prevGifs => currentOffset === 0 ? data : [...prevGifs, ...data]);
      setOffset(currentOffset + data.length);
      setHasMore(!!pagination && pagination.total_count > currentOffset + data.length);
      setError(null);
    } catch (err) {
      console.error('Error searching gifs:', err);
      setError('Failed to search GIFs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [fetchTrendingGifs]);

  // Load trending gifs on initial render
  useEffect(() => {
    if (open) {
      setGifs([]);
      setOffset(0);
      setHasMore(true);
      setSearchQuery('');
      fetchTrendingGifs();
    }
  }, [open, fetchTrendingGifs]);

  const handleSearch = (e) => {
    const text = e.target.value;
    setSearchQuery(text);
    setGifs([]);
    setOffset(0);
    searchGifs(text);
  };

  const handleGifClick = (gif) => {
    onGifSelect(gif);
    onClose();
  };

  const handleClickAway = () => {
    onClose();
  };

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight * 1.5 && !loading && hasMore) {
      if (searchQuery) {
        searchGifs(searchQuery, offset);
      } else {
        fetchTrendingGifs(offset);
      }
    }
  };

  if (!open || !anchorEl) return null;

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={position}
        style={{ zIndex: 1300 }}
        modifiers={[
          {
            name: 'flip',
            enabled: true,
            options: { altBoundary: true, fallbackPlacements: ['top-start', 'bottom-start', 'top-end', 'bottom-end'] },
          },
          {
            name: 'preventOverflow',
            enabled: true,
            options: { altAxis: false, altBoundary: true, tether: false },
          },
        ]}
      >
        <Paper
          sx={{
            width: 520,
            maxWidth: '90vw',
            height: '70vh',
            maxHeight: '700px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="h6" component="span" sx={{ fontSize: '1.25rem', fontWeight: 600 }}>
              Choose a GIF
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Input */}
          <Box sx={{ px: 2, pt: 1, pb: 2 }}>
            <TextField
              fullWidth
              placeholder="Search GIFs..."
              value={searchQuery}
              onChange={handleSearch}
              size="small"
              autoFocus
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Box>

          {/* GIF Grid */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              px: 2,
            }}
            onScroll={handleScroll}
          >
            {loading && gifs.length === 0 ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <Typography color="text.secondary">{error}</Typography>
              </Box>
            ) : (
              <>
                <ImageList cols={3} gap={8} sx={{ mb: 0 }}>
                  {gifs.map((gif, index) => (
                    <ImageListItem
                      key={`${gif.id}-${index}`}
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 2,
                        overflow: 'hidden',
                        transition: 'transform 0.2s',
                        '&:hover': {
                          transform: 'scale(1.05)',
                        },
                      }}
                      onClick={() => handleGifClick(gif)}
                    >
                      <img
                        src={gif.images?.fixed_width?.url || gif.images?.original?.url}
                        alt={gif.title}
                        loading="lazy"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </ImageListItem>
                  ))}
                </ImageList>

                {loading && gifs.length > 0 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </>
            )}
          </Box>

          {/* Powered by GIPHY */}
          <Box sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Powered by GIPHY
            </Typography>
          </Box>
        </Paper>
      </Popper>
    </ClickAwayListener>
  );
};

export default GifPicker;
